import {
  insertV14Comment,
  insertNoteToDraft,
  insertCompanyComment,
} from './features/text-editing/actions/comments';
import { insertSimplePlaceholder } from './features/text-editing/actions/placeholders';
import { addDefinedTerm } from './features/text-editing/actions/definitions';
import { insertSelfReference } from './features/text-editing/actions/crossReferences';
import { insertExhibit } from './features/text-editing/actions/tables';
import { getSelectionText } from './utils/office/selection';
import { promptTermViaDialog } from './utils/office/termEntryDialog';
import { focusDocumentWindow } from './utils/office/focus';

/**
 * "Add defined term" from the ribbon button or keyboard shortcut: with a
 * selection, insert directly; without one, ask via the overlay dialog — the
 * same flow as the pane button (previously this path just threw "No text
 * available" into the console and looked dead).
 */
async function definedTermFlow(): Promise<void> {
  const sel = (await getSelectionText()).trim();
  if (sel) {
    await addDefinedTerm(sel);
    return;
  }
  try {
    const term = await promptTermViaDialog();
    if (term) await addDefinedTerm(term);
  } catch (e) {
    // No pane form available in this runtime — log rather than die silently.
    console.warn('[V14 actions] term dialog unavailable:', e);
  }
}

type AddinEvent = Office.AddinCommands.Event;

// Ribbon ExecuteFunction invocations pass an event that MUST be completed so
// Word knows the command finished. KEYBOARD-SHORTCUT invocations call the
// handler with NO event (null) — completing must be conditional (observed
// live: the first successful shortcut dispatch crashed on event.completed).
function makeHandler(fn: () => Promise<void>) {
  return async (event?: AddinEvent | null) => {
    try {
      await fn();
    } catch (e) {
      console.error('[V14 actions]', e);
    } finally {
      // Hand keyboard focus to the document, so the user types at the caret the
      // action just placed. The pane's own runAction() does this for every pane
      // button; the ribbon and shortcut paths run here instead and had no
      // equivalent — and now that the placeholder actions move the caret
      // (issues #20/#21), a caret with focus left behind in the pane is exactly
      // the Windows double-insert trap utils/office/focus.ts documents.
      // Best-effort and never throws, so it cannot mask the action's own error.
      await focusDocumentWindow();
      event?.completed?.();
    }
  };
}

let registered = false;

/**
 * Registers all shortcut/ribbon actions via Office.actions.associate.
 *
 * MUST run in the page the manifest's <Runtimes> shared runtime loads
 * (Taskpane.Url → index.html): keyboard shortcuts (ExtendedOverrides) are only
 * dispatched into the SHARED runtime — without this, the shortcuts silently do
 * nothing (observed live; the classic FunctionFile runtime masked it for
 * ribbon clicks). Also imported by functions.ts as a fallback for hosts that
 * still load the classic function file.
 */
export function registerOfficeActions(): void {
  if (registered || typeof Office === 'undefined' || !Office.actions?.associate) return;
  registered = true;
  Office.actions.associate('insertV14Comment', makeHandler(insertV14Comment));
  Office.actions.associate('insertNoteToDraft', makeHandler(insertNoteToDraft));
  Office.actions.associate('insertPlaceholder', makeHandler(() => insertSimplePlaceholder('…')));
  Office.actions.associate('insertDefinedTerm', makeHandler(definedTermFlow));
  Office.actions.associate('insertCompanyComment', makeHandler(insertCompanyComment));
  Office.actions.associate('insertSelfReference', makeHandler(insertSelfReference));
  // Exhibit with sensible defaults (the dialog's type/description aren't
  // available from a keyboard shortcut).
  Office.actions.associate('insertExhibit', makeHandler(() => insertExhibit('Anlage', '[…]')));
}
